const V1_API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api/v1'
  : 'http://localhost:7777/api/v1';

const PUBLIC_API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : 'http://localhost:7777/api';

type TtmlMutationKind = 'add' | 'update' | 'remove';
type TtmlMutableSegmentType = 'clips' | 'edits' | 'references';

interface StructuredSegmentBase {
  index: number;
  startTime: number;
  endTime: number;
  startTimestamp: string;
  endTimestamp: string;
  duration: number;
  raw: string;
  format?: string;
}

interface ApiErrorPayload {
  error?: string;
  message?: string;
}

export interface StructuredScoreBreakdownEntry {
  score: number;
  max: number;
}

export interface StructuredTopic extends StructuredSegmentBase {
  segmentNumber: number | null;
  qualityRank: number | null;
  totalTopics: number | null;
  title: string | null;
  score: number | null;
  scoreBreakdown: Record<string, StructuredScoreBreakdownEntry> | null;
  justification: string | null;
  optimalPlacement: number | null;
  continuity: string | null;
  isPlaceholder: boolean;
}

export interface StructuredClip extends StructuredSegmentBase {
  title: string | null;
  clipType: string | null;
  description: string | null;
  visual: string | null;
  isPlaceholder?: boolean;
}

export interface StructuredEdit extends StructuredSegmentBase {
  title: string | null;
  suggestion: string | null;
  improvement: string | null;
  priority: string | null;
}

export interface StructuredReference extends StructuredSegmentBase {
  name: string | null;
  linkText: string | null;
  url: string | null;
  category: string | null;
  context: string | null;
  visual: string | null;
}

export interface TtmlStructureSummary {
  topicCount: number;
  clipCount: number;
  editCount: number;
  referenceCount: number;
}

export interface TtmlStructureResponse {
  episodeId: string;
  checksum: string;
  version?: number;
  topics: StructuredTopic[];
  clips: StructuredClip[];
  edits: StructuredEdit[];
  references: StructuredReference[];
  summary?: TtmlStructureSummary;
}

export interface StructuredMutationResponse {
  checksum: string;
  version: number;
  parentChecksum: string;
  rootChecksum: string;
  isSelected?: boolean;
  isValid?: boolean;
  validationWarnings?: string[];
  message?: string;
}

export interface TtmlMutationOperation<TData> {
  op: TtmlMutationKind;
  index?: number;
  data?: TData;
}

export interface StructuredClipDraft {
  startTime?: number;
  endTime?: number;
  title?: string;
  clipType?: string;
  description?: string;
  visual?: string;
}

export interface StructuredEditDraft {
  startTime?: number;
  endTime?: number;
  title?: string;
  suggestion?: string;
  improvement?: string;
  priority?: string;
}

export interface StructuredReferenceDraft {
  startTime?: number;
  endTime?: number;
  name?: string;
  linkText?: string;
  url?: string;
  category?: string;
  context?: string;
  visual?: string;
}

interface MutationRequestBody<TData> {
  operations: Array<TtmlMutationOperation<TData>>;
  revisionNotes?: string;
  autoSelect: boolean;
}

const buildHeaders = (token?: string, headers?: HeadersInit): Headers => {
  const requestHeaders = new Headers(headers);
  requestHeaders.set('Content-Type', 'application/json');

  if (token) {
    requestHeaders.set('Authorization', `Bearer ${token}`);
  }

  return requestHeaders;
};

const parseErrorMessage = async (response: Response): Promise<string> => {
  const responseClone = response.clone();

  try {
    const payload = await response.json() as ApiErrorPayload;
    if (payload.error) {
      return payload.error;
    }
    if (payload.message) {
      return payload.message;
    }
  } catch {
    const text = await responseClone.text().catch(() => '');
    if (text) {
      return text;
    }
  }

  return `HTTP ${response.status}`;
};

const requestJson = async <T>(
  path: string,
  token?: string,
  options: RequestInit = {},
): Promise<T> => {
  const response = await fetch(`${V1_API_BASE_URL}${path}`, {
    ...options,
    headers: buildHeaders(token, options.headers),
  });

  if (!response.ok) {
    throw new Error(await parseErrorMessage(response));
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
};

const mutateSegment = async <TData>(
  sessionId: string,
  checksum: string,
  segmentType: TtmlMutableSegmentType,
  operations: Array<TtmlMutationOperation<TData>>,
  revisionNotes?: string,
  token?: string,
): Promise<StructuredMutationResponse> => {
  const body: MutationRequestBody<TData> = {
    operations,
    revisionNotes,
    autoSelect: true,
  };

  return requestJson<StructuredMutationResponse>(
    `/episodes/${encodeURIComponent(sessionId)}/ttml/versions/${encodeURIComponent(checksum)}/${segmentType}/mutate`,
    token,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );
};

export const TtmlMetadataService = {
  getStructure(sessionId: string, checksum: string, token?: string): Promise<TtmlStructureResponse> {
    return requestJson<TtmlStructureResponse>(
      `/episodes/${encodeURIComponent(sessionId)}/ttml/versions/${encodeURIComponent(checksum)}/structure`,
      token,
    );
  },

  async getPublicStructure(shareToken: string, checksum: string): Promise<TtmlStructureResponse> {
    const response = await fetch(
      `${PUBLIC_API_BASE_URL}/public/share/${encodeURIComponent(shareToken)}/ttml-versions/${encodeURIComponent(checksum)}/structure`,
      {
        headers: {
          Accept: 'application/json',
        },
        cache: 'no-store',
        credentials: 'omit',
      },
    );

    if (!response.ok) {
      throw new Error(await parseErrorMessage(response));
    }

    return response.json() as Promise<TtmlStructureResponse>;
  },

  mutateClips(
    sessionId: string,
    checksum: string,
    operations: Array<TtmlMutationOperation<StructuredClipDraft>>,
    revisionNotes?: string,
    token?: string,
  ): Promise<StructuredMutationResponse> {
    return mutateSegment(sessionId, checksum, 'clips', operations, revisionNotes, token);
  },

  mutateEdits(
    sessionId: string,
    checksum: string,
    operations: Array<TtmlMutationOperation<StructuredEditDraft>>,
    revisionNotes?: string,
    token?: string,
  ): Promise<StructuredMutationResponse> {
    return mutateSegment(sessionId, checksum, 'edits', operations, revisionNotes, token);
  },

  mutateReferences(
    sessionId: string,
    checksum: string,
    operations: Array<TtmlMutationOperation<StructuredReferenceDraft>>,
    revisionNotes?: string,
    token?: string,
  ): Promise<StructuredMutationResponse> {
    return mutateSegment(sessionId, checksum, 'references', operations, revisionNotes, token);
  },
};
